import { useState } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { apiGet, apiDelete, apiDownload, ApiError } from '../api/client';
import { formatMkd, formatDate, todayIso, monthAgoIso } from '../lib/format';
import FilterBar, { btn } from '../components/FilterBar';
import RrogaModal from '../components/RrogaModal';
import ConfirmDialog from '../components/ConfirmDialog';
import { useToast } from '../components/Toast';

/**
 * Rrogat. Ports frmRrogat, which served both the actual and planned tables
 * through a `dname` string — same split here via `variant`.
 */

interface RrogaRow {
  id: number;
  sID: number | null;
  fullName: string | null;
  roli: string | null;
  biznesi: string | null;
  date: string;
  rbaz: number;
  rbonus: number;
  rzbritje: number;
  ravans: number;
  paid: number;
}

export default function Rrogat({ variant }: { variant: 'rrogat' | 'rrogatPlan' }) {
  const queryClient = useQueryClient();
  const toast = useToast();

  const [search, setSearch] = useState('');
  const [startDate, setStartDate] = useState(monthAgoIso);
  const [endDate, setEndDate] = useState(todayIso);
  const [editing, setEditing] = useState<{ id: number | null } | null>(null);
  const [deleting, setDeleting] = useState<RrogaRow | null>(null);

  const listQuery = useQuery({
    queryKey: ['rrogat', variant, search, startDate, endDate],
    queryFn: () =>
      apiGet<{ rows: RrogaRow[]; total: number; monthTotal: number }>(`/rrogat/${variant}`, {
        search,
        startDate,
        endDate,
      }),
    placeholderData: (prev) => prev,
  });

  function refresh() {
    void queryClient.invalidateQueries({ queryKey: ['rrogat'] });
  }

  const deleteMutation = useMutation({
    mutationFn: (id: number) => apiDelete(`/rrogat/${variant}/${id}`),
    onSuccess: () => {
      toast.success('Rreshti u fshi me sukses.');
      refresh();
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Fshirja dështoi.'),
  });

  const rows = listQuery.data?.rows ?? [];
  const isActual = variant === 'rrogat';

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">
            {isActual ? 'Rrogat' : 'Rrogat e planifikuara'}
          </h1>
          <p className="text-sm text-muted">
            Ky muaj:{' '}
            <span className="tnum font-medium text-ink">
              {formatMkd(listQuery.data?.monthTotal ?? 0)}
            </span>
          </p>
        </div>
      </div>

      <FilterBar
        search={search}
        onSearchChange={setSearch}
        startDate={startDate}
        onStartDateChange={setStartDate}
        endDate={endDate}
        onEndDateChange={setEndDate}
        searchLabel="Kërko punëtor ose rol"
      >
        <button
          type="button"
          onClick={() =>
            apiDownload(`/rrogat/${variant}/eksport`, { search, startDate, endDate }).catch((err) =>
              toast.error(err instanceof ApiError ? err.message : 'Eksportimi dështoi.'),
            )
          }
          disabled={rows.length === 0}
          className={btn.secondary}
        >
          Eksporto Excel
        </button>
        <button type="button" onClick={() => setEditing({ id: null })} className={btn.primary}>
          Shto pagesë
        </button>
      </FilterBar>

      <div className="overflow-hidden rounded-xl bg-white">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[880px] text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wide text-muted">
                <th className="px-4 py-3 font-semibold">#</th>
                <th className="px-4 py-3 font-semibold">Data</th>
                <th className="px-4 py-3 font-semibold">Punëtori</th>
                <th className="px-4 py-3 font-semibold">Biznesi</th>
                <th className="px-4 py-3 text-right font-semibold">Baza</th>
                <th className="px-4 py-3 text-right font-semibold">Bonusi</th>
                <th className="px-4 py-3 text-right font-semibold">Zbritja</th>
                <th className="px-4 py-3 text-right font-semibold">Avansi</th>
                <th className="px-4 py-3 text-right font-semibold">Totali</th>
                <th className="px-4 py-3 text-right font-semibold">Veprime</th>
              </tr>
            </thead>
            <tbody>
              {listQuery.isLoading && (
                <tr>
                  <td colSpan={10} className="px-4 py-10 text-center text-muted">
                    Duke ngarkuar…
                  </td>
                </tr>
              )}

              {!listQuery.isLoading && rows.length === 0 && (
                <tr>
                  <td colSpan={10} className="px-4 py-10 text-center text-muted">
                    Nuk ka të dhëna për këtë periudhë.
                  </td>
                </tr>
              )}

              {rows.map((row, index) => (
                <tr key={row.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50">
                  <td className="px-4 py-2.5 text-muted">{index + 1}</td>
                  <td className="px-4 py-2.5 whitespace-nowrap">{formatDate(row.date)}</td>
                  <td className="px-4 py-2.5">
                    {row.fullName ?? (
                      // Two live rows reference a staff member that no longer
                      // exists; an inner join would have hidden them.
                      <span className="text-amber-700" title={`sID ${row.sID ?? '—'}`}>
                        (punëtor i fshirë)
                      </span>
                    )}
                    {row.roli && <span className="block text-xs text-muted">{row.roli}</span>}
                  </td>
                  <td className="px-4 py-2.5 text-muted">{row.biznesi ?? '—'}</td>
                  <td className="tnum px-4 py-2.5 text-right">{formatMkd(row.rbaz)}</td>
                  <td className="tnum px-4 py-2.5 text-right text-emerald-700">
                    +{formatMkd(row.rbonus)}
                  </td>
                  <td className="tnum px-4 py-2.5 text-right text-red-600">
                    −{formatMkd(row.rzbritje)}
                  </td>
                  <td className="tnum px-4 py-2.5 text-right">{formatMkd(row.ravans)}</td>
                  <td className="tnum px-4 py-2.5 text-right font-medium">{formatMkd(row.paid)}</td>
                  <td className="px-4 py-2.5">
                    <div className="flex justify-end gap-1">
                      <button
                        type="button"
                        onClick={() => setEditing({ id: row.id })}
                        className="rounded-lg px-2 py-1 text-xs font-medium text-slate-600 hover:bg-slate-100"
                      >
                        Ndrysho
                      </button>
                      <button
                        type="button"
                        onClick={() => setDeleting(row)}
                        className="rounded-lg px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-50"
                      >
                        Fshij
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-slate-200 bg-slate-50 px-4 py-3">
          <span className="text-sm text-muted">
            Shuma totale nga {formatDate(startDate)} deri më {formatDate(endDate)}:
          </span>
          <span className="tnum text-lg font-semibold text-ink">
            {formatMkd(listQuery.data?.total ?? 0)}
          </span>
        </div>
      </div>

      {editing && (
        <RrogaModal
          variant={variant}
          rowId={editing.id}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            toast.success('Të dhënat u ruajtën me sukses!');
            refresh();
          }}
        />
      )}

      {deleting && (
        <ConfirmDialog
          title="Konfirmo fshirjen"
          message="A jeni i sigurt që dëshironi të fshini këtë rresht?"
          confirmLabel="Fshij"
          destructive
          busy={deleteMutation.isPending}
          onCancel={() => setDeleting(null)}
          onConfirm={() => {
            deleteMutation.mutate(deleting.id);
            setDeleting(null);
          }}
        />
      )}
    </div>
  );
}
